/**
 * @fileoverview BMaps Atom[] -> mmCIF text for PyMOL's `cmd.load_raw(text, 'cif', object)`.
 *
 * PROVENANCE: ported from pymol-web `js/cif-writer.js` (wrapper API 0.1.0, itself a port of
 * pymol-web `spikes/0a-api-wheel/cif_writer_sketch.js`). That file takes plain atom records;
 * this one reads BMaps `Atom` / `Bond` objects. It lives in bmaps, and is unit-tested here,
 * because bmaps CI has no payload to import it from. Keep the two in step when the column set
 * changes.
 *
 * What PyMOL's reader (layer2/CifMoleculeReader.cpp) does with it - verified in pymol-web
 * spike 0A and the Phase 1.4 seam tests unless marked otherwise:
 *   - `_atom_site.id` becomes the atom's `ID`, verbatim (gaps and any order are fine).
 *   - with `cif_use_auth` (default) chain / residue number come from `auth_*`.
 *   - a `_pymol_bond` loop means "these bonds and no others": no distance-based bonding.
 *     Every id in it must resolve, or PyMOL silently bonds atom 0 to itself - so a bond
 *     whose partner is not in this block is DROPPED here.
 *   - nulls must be the bare tokens `.` / `?`; a quoted '.' is the one-character string.
 *   - a NaN coordinate crashed the page in the spike, so such atoms are left out.
 *   - per-atom `_atom_site.pymol_ss` ('H', 'S', 'L') carries BMaps' secondary structure.
 *     With none given PyMOL would run its own `dss`, so every row gets one.
 *   - `pdbx_PDB_model_num` values are mapped to consecutive states in order of appearance
 *     (read from the reader source, `pdb_honor_model_number` off). The caller keeps one model
 *     number per object so every object has a single state.
 */

import { VisualizerInterface } from '../VisualizerInterface';

const RESERVED = /^(data_|loop_|save_|global_|stop_)/i;

/**
 * Quote one CIF value: a bare word when possible, else quoted, else a text field.
 * Empty / missing values become the bare null token.
 * @param {*} value
 * @param {'.'|'?'} [nullToken]
 * @returns {string}
 */
export function cifQuote(value, nullToken='.') {
    if (value === undefined || value === null || value === '') return nullToken;
    const text = String(value);
    if (text === '.' || text === '?') return `'${text}'`;
    if (!/[\s'"]/.test(text) && !/^[_#$[\];]/.test(text) && !RESERVED.test(text)) return text;
    if (!text.includes("'")) return `'${text}'`;
    if (!/"\s/.test(text) && !text.endsWith('"')) return `"${text}"`;
    return `\n;${text}\n;\n`;
}

const fixed3 = (n) => (Math.round(n * 1000) / 1000).toFixed(3);
const fixed2 = (n) => (Math.round(n * 100) / 100).toFixed(2);
const finiteOr = (n, fallback) => (Number.isFinite(n) ? n : fallback);

/**
 * BMaps bond order -> `_pymol_bond.order` (1, 2, 3, or 4 = aromatic).
 * BMaps passes 7 (delocalized) and 8 / 9 (aromatic) through to the visualizer
 * (model/atoms.js Bond.updateBondOrder, util/chem_utils.js).
 */
export function pymolBondOrder(order) {
    if (order === 1 || order === 2 || order === 3) return order;
    if (order === 4 || order === 7 || order === 8 || order === 9) return 4;
    return 1;
}

/**
 * Secondary structure lookup with a per-call cache. The default is what Visualizer3Dmol
 * uses, `VisualizerInterface.getAtomProteinResidue(atom).getSecondaryStructureInfo()`; that
 * walks all residues of a chain for every atom, so chains are indexed once per call.
 * @returns {(atom: object) => ('H'|'S'|'L')}
 */
export function makeSecondaryStructureLookup() {
    const chainIndexes = new Map(); // Polymer -> Map(atom -> Residue)
    const residueFor = (atom) => {
        if (atom.hetflag) return null;
        const group = typeof atom.getAtomGroup === 'function' ? atom.getAtomGroup() : null;
        if (group && typeof group.getResidues === 'function') {
            let index = chainIndexes.get(group);
            if (!index) {
                index = new Map();
                for (const residue of group.getResidues()) {
                    for (const resAtom of residue.getAtoms()) index.set(resAtom, residue);
                }
                chainIndexes.set(group, index);
            }
            return index.get(atom) || null;
        }
        return VisualizerInterface.getAtomProteinResidue(atom);
    };
    return (atom) => {
        const residue = residueFor(atom);
        const info = residue && typeof residue.getSecondaryStructureInfo === 'function'
            ? residue.getSecondaryStructureInfo()
            : null;
        if (info && info.type === 'helix') return 'H';
        if (info && info.type === 'sheet') return 'S';
        return 'L';
    };
}

const AtomSiteColumns = [
    'group_PDB', 'id', 'type_symbol', 'label_atom_id', 'label_alt_id', 'label_comp_id',
    'label_asym_id', 'label_seq_id', 'pdbx_PDB_ins_code', 'Cartn_x', 'Cartn_y', 'Cartn_z',
    'occupancy', 'B_iso_or_equiv', 'pdbx_formal_charge', 'auth_asym_id', 'auth_seq_id',
    'pdbx_PDB_model_num', 'pymol_ss',
];

/**
 * @param {object[]} atoms BMaps atoms, in the order they should have inside the PyMOL object
 * @param {object} options
 * @param {(atom: object) => (number|undefined)} options.idOf backend id of an atom. Atoms for
 *   which it returns nothing are left out, as are bonds to them.
 * @param {string} [options.name] data block name
 * @param {(atom: object) => string} [options.ssOf] secondary structure lookup ('H' | 'S' | 'L')
 * @param {boolean} [options.bonds=true] false: no `_pymol_bond` loop (PyMOL then bonds by
 *   distance; for objects whose bonds are never shown, e.g. surfaces)
 * @param {boolean} [options.oneModel=false] write model number 1 for every atom, so the object
 *   has exactly one state whatever the atoms' own model numbers
 * @returns {{
 *   text: string, atomCount: number, bondCount: number,
 *   droppedBonds: number, skippedAtoms: object[],
 * }}
 *   `droppedBonds` counts bonds whose partner is not in this block (PyMOL has no bonds between
 *   objects); `skippedAtoms` are atoms without an id or with a non-finite coordinate.
 */
export function cifFromAtoms(atoms, {
    idOf, name='bmaps', ssOf=makeSecondaryStructureLookup(), bonds: writeBonds=true, oneModel=false,
    positionOf=null,
}) {
    const out = [`data_${String(name).replace(/\s/g, '_')}`, '#', 'loop_'];
    for (const column of AtomSiteColumns) out.push(`_atom_site.${column}`);

    const localIds = new Map(); // atom -> id, only the atoms written to this block
    const skippedAtoms = [];
    for (const atom of atoms) {
        const id = idOf(atom);
        // Coordinates ONLY through getPosition(): it applies the alignment transform. A
        // caller's positionOf() supplies positions it captured that way earlier.
        const [x, y, z] = positionOf ? positionOf(atom) : atom.getPosition();
        if (!Number.isInteger(id) || localIds.has(atom)
            || !(Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z))) {
            skippedAtoms.push(atom);
            continue;
        }
        localIds.set(atom, id);

        const het = !!atom.hetflag;
        const chain = cifQuote(atom.chain);
        const resi = Number.isInteger(atom.resi) ? atom.resi : cifQuote(atom.resi, '?');
        // Only a well-formed symbol: BMaps uses '??' for an unknown element, and PyMOL guesses
        // from the atom name when the symbol is null.
        const elem = /^[A-Za-z]{1,2}$/.test(atom.elem || '') ? atom.elem : null;
        // BMaps' atom.charge is a PARTIAL charge; only an integer formalCharge is written.
        const charge = Number.isInteger(atom.formalCharge) ? atom.formalCharge : '?';
        const model = !oneModel && Number.isInteger(atom.modelNum) && atom.modelNum > 0
            ? atom.modelNum : 1;
        out.push([
            het ? 'HETATM' : 'ATOM',
            id,
            cifQuote(elem, '?'),
            cifQuote(atom.atom),
            cifQuote(atom.altLoc),
            cifQuote(atom.resn),
            chain,
            het ? '.' : resi,
            cifQuote(atom.icode, '?'),
            fixed3(x), fixed3(y), fixed3(z),
            '1.00',
            fixed2(finiteOr(atom.Bfactor, 0)),
            charge,
            chain,
            resi,
            model,
            ssOf(atom),
        ].join(' '));
    }
    out.push('#');

    const seenBonds = new Set();
    const bondRows = [];
    let droppedBonds = 0;
    for (const [atom, id] of (writeBonds ? localIds : [])) {
        const bonds = typeof atom.getBonds === 'function' ? atom.getBonds() : (atom.bonds || []);
        for (const bond of bonds) {
            if (seenBonds.has(bond)) continue;
            seenBonds.add(bond);
            const other = bond.atom1 === atom ? bond.atom2 : bond.atom1;
            const otherId = localIds.get(other);
            if (otherId === undefined || other === atom) {
                droppedBonds++;
            } else {
                bondRows.push(`${id} ${otherId} ${pymolBondOrder(bond.order)}`);
            }
        }
    }
    if (bondRows.length > 0) {
        out.push('loop_', '_pymol_bond.atom_site_id_1', '_pymol_bond.atom_site_id_2',
            '_pymol_bond.order');
        for (const row of bondRows) out.push(row);
        out.push('#');
    }

    return {
        text: `${out.join('\n')}\n`,
        atomCount: localIds.size,
        bondCount: bondRows.length,
        droppedBonds,
        skippedAtoms,
    };
}
